import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LayoutDashboard, DollarSign, Building2, Users, Package, FileText, Handshake, LogOut, Globe, Settings, BarChart3 } from 'lucide-react';
import { Button } from './ui/button';
import { canAccessPage } from '../utils/permissions';

export const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, toggleLanguage, language } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);

  const allNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('dashboard') },
    { path: '/finance', icon: DollarSign, label: t('finance') },
    { path: '/clients-suppliers', icon: Building2, label: t('clientsAndSuppliers') },
    { path: '/inventory', icon: Package, label: t('inventory') },
    { path: '/invoicing', icon: FileText, label: t('invoicing') },
    { path: '/hrm', icon: Users, label: t('hrm') },
    { path: '/outsourcing', icon: Handshake, label: t('outsourcing') },
    { path: '/reports', icon: BarChart3, label: t('reports') },
    { path: '/settings', icon: Settings, label: t('settings') },
  ];

  const navItems = allNavItems.filter(item => canAccessPage(user?.role, item.path));

  return (
    <div
      className={`${isExpanded ? 'w-64' : 'w-14'} min-h-screen bg-primary text-primary-foreground flex flex-col transition-all duration-200 overflow-hidden shrink-0`}
      style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="20" height="20" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M0 0h20v20H0z" fill="%230F172A"/%3E%3Cpath d="M0 0h10v10H0z" fill="%23000000" fill-opacity="0.03"/%3E%3C/svg%3E")'}}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <div className="p-3 border-b border-slate-700 min-h-[72px] flex flex-col justify-center">
        {isExpanded ? (
          <>
            <h1 className="text-xl font-bold tracking-tight leading-tight whitespace-nowrap" data-testid="app-title">
              CLADAKIS
              <span className="block text-xs font-normal text-slate-400 mt-0.5">{t('forReadyMadeGarments')}</span>
            </h1>
            <p className="text-xs text-slate-400 mt-2 whitespace-nowrap" lang={language === 'ar' ? 'ar' : 'en'}>
              {user?.name} ({user?.role})
            </p>
          </>
        ) : (
          <div className="flex justify-center">
            <span className="text-sm font-bold text-slate-300">C</span>
          </div>
        )}
      </div>

      <nav className="flex-1 p-2 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.slice(1)}`}
              className={`flex items-center ${isExpanded ? 'gap-3 px-4' : 'justify-center px-0'} py-3 rounded-md transition-colors ${
                isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-slate-800 text-slate-300'
              }`}
            >
              <Icon className="w-5 h-5 shrink-0" />
              {isExpanded && (
                <span className="text-sm font-medium whitespace-nowrap" lang={language === 'ar' ? 'ar' : 'en'}>{item.label}</span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="p-2 border-t border-slate-700 space-y-1">
        <Button
          variant="ghost"
          className={`w-full ${isExpanded ? 'justify-start gap-3' : 'justify-center px-0'} text-slate-300 hover:bg-slate-800`}
          onClick={toggleLanguage}
          data-testid="language-toggle-btn"
        >
          <Globe className="w-5 h-5 shrink-0" />
          {isExpanded && <span className="text-sm whitespace-nowrap">{language === 'en' ? 'العربية' : 'English'}</span>}
        </Button>
        <Button
          variant="ghost"
          className={`w-full ${isExpanded ? 'justify-start gap-3' : 'justify-center px-0'} text-slate-300 hover:bg-slate-800`}
          onClick={logout}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 shrink-0" />
          {isExpanded && <span className="text-sm whitespace-nowrap" lang={language === 'ar' ? 'ar' : 'en'}>{t('logout')}</span>}
        </Button>
      </div>
    </div>
  );
};
